import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { Print } from '@mui/icons-material';
import { printHtml } from '../services/printService';
import { toast } from 'react-toastify';

export default function PackingSlipButton({ 
  order, 
  variant = 'outlined',
  color = 'primary',
  size = 'medium',
  showIcon = true,
  label = 'Packing Slip'
}) {
  const [generating, setGenerating] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const calculateTotals = () => {
    if (!order?.lines) return { totalItems: 0, totalWeight: 0 };
    
    let totalItems = 0;
    let totalWeight = 0;
    
    order.lines.forEach(line => {
      const qty = toNumber(line.quantity_ordered) || toNumber(line.quantity_allocated) || toNumber(line.quantity);
      if (qty > 0) {
        totalItems += qty;
        totalWeight += qty * toNumber(line.product_weight);
      }
    });
    
    return { totalItems, totalWeight };
  };

  const getShippableLines = () => {
    if (!order?.lines) return [];
    return order.lines.map(line => {
      const qty = toNumber(line.quantity_ordered) || toNumber(line.quantity_allocated) || toNumber(line.quantity);
      const weight = toNumber(line.product_weight);
      
      return {
        ...line,
        toShip: qty,
        productWeight: weight,
        lineWeight: qty * weight
      };
    }).filter(line => line.toShip > 0);
  };

  const getDeliveryAddress = () => {
    const parts = [];
    if (order.delivery_name) parts.push(order.delivery_name);
    if (order.delivery_line_1) parts.push(order.delivery_line_1);
    if (order.delivery_line_2) parts.push(order.delivery_line_2);
    if (order.delivery_line_3) parts.push(order.delivery_line_3);
    if (order.delivery_city) parts.push(order.delivery_city);
    if (order.delivery_county) parts.push(order.delivery_county);
    if (order.delivery_postcode) parts.push(order.delivery_postcode);
    
    if (parts.length === 0 && order.delivery_address) {
      return order.delivery_address;
    }
    
    return parts.join('<br>');
  };

  const buildHtmlContent = () => {
    const { totalItems, totalWeight } = calculateTotals();
    const shippableLines = getShippableLines();
    const deliveryAddress = getDeliveryAddress();
    const today = new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });

    // Note: Removed the window.print() script since printService handles that
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Packing Slip - ${order.order_number}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            line-height: 1.4;
            color: #333;
            padding: 20px;
          }
          
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 2px solid #333;
          }
          
          .company-info {
            flex: 1;
          }
          
          .company-name {
            font-size: 24px;
            font-weight: bold;
            color: #1976d2;
            margin-bottom: 5px;
          }
          
          .document-title {
            text-align: right;
          }
          
          .document-title h1 {
            font-size: 28px;
            color: #333;
            margin-bottom: 5px;
          }
          
          .order-number {
            font-size: 18px;
            font-weight: bold;
            color: #1976d2;
          }
          
          .info-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
            gap: 20px;
          }
          
          .info-box {
            flex: 1;
            border: 1px solid #ddd;
            padding: 15px;
            border-radius: 5px;
          }
          
          .info-box h3 {
            font-size: 14px;
            color: #666;
            margin-bottom: 10px;
            text-transform: uppercase;
            border-bottom: 1px solid #eee;
            padding-bottom: 5px;
          }
          
          .info-box p {
            margin-bottom: 5px;
          }
          
          .info-box .highlight {
            font-weight: bold;
            font-size: 14px;
          }
          
          .dates-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
            margin-bottom: 20px;
          }
          
          .date-box {
            border: 1px solid #ddd;
            padding: 10px;
            border-radius: 5px;
            text-align: center;
          }
          
          .date-box label {
            display: block;
            font-size: 11px;
            color: #666;
            text-transform: uppercase;
            margin-bottom: 5px;
          }
          
          .date-box .value {
            font-size: 14px;
            font-weight: bold;
          }
          
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          
          .items-table th {
            background-color: #1976d2;
            color: white;
            padding: 10px 8px;
            text-align: left;
            font-size: 12px;
            text-transform: uppercase;
          }
          
          .items-table th.right {
            text-align: right;
          }
          
          .items-table th.center {
            text-align: center;
          }
          
          .items-table td {
            padding: 10px 8px;
            border-bottom: 1px solid #ddd;
          }
          
          .items-table td.right {
            text-align: right;
          }
          
          .items-table td.center {
            text-align: center;
          }
          
          .items-table tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          
          .product-code {
            font-weight: bold;
            color: #1976d2;
          }
          
          .totals-section {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 20px;
          }
          
          .totals-box {
            border: 2px solid #1976d2;
            padding: 15px;
            border-radius: 5px;
            min-width: 250px;
          }
          
          .totals-row {
            display: flex;
            justify-content: space-between;
            padding: 5px 0;
          }
          
          .totals-row.total {
            border-top: 1px solid #ddd;
            margin-top: 5px;
            padding-top: 10px;
            font-weight: bold;
            font-size: 14px;
          }
          
          .notes-section {
            margin-top: 20px;
            padding: 15px;
            border: 1px solid #ddd;
            border-radius: 5px;
            background-color: #f9f9f9;
          }
          
          .notes-section h3 {
            font-size: 12px;
            color: #666;
            margin-bottom: 10px;
          }
          
          .delivery-details {
            margin-top: 25px;
            padding: 15px;
            border: 2px solid #333;
            border-radius: 5px;
            background-color: #f9f9f9;
            text-align: center;
          }

          .delivery-details h3 {
            font-size: 14px;
            font-weight: bold;
            text-transform: uppercase;
            margin-bottom: 10px;
            color: #333;
          }

          .delivery-details p {
            font-size: 11px;
            margin-bottom: 6px;
            color: #333;
          }

          .delivery-details .warning {
            font-weight: bold;
            font-size: 12px;
            margin-top: 10px;
            text-transform: uppercase;
          }

          .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 10px;
            color: #666;
          }
          
          @page {
            size: A4 portrait;
            margin: 10mm;
          }

          @media print {
            body {
              padding: 0;
              font-size: 11px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-info">
            <div class="company-name">Gopak</div>
            <p>Range Road, Hythe</p>
            <p>Kent, CT21 6HG</p>
          </div>
          <div class="document-title">
            <h1>PACKING SLIP</h1>
            <div class="order-number">${order.order_number}</div>
          </div>
        </div>
        
        <div class="info-section">
          <div class="info-box">
            <h3>Ship To</h3>
            <p class="highlight">${deliveryAddress || 'No delivery address specified'}</p>
          </div>
          <div class="info-box">
            <h3>Customer Details</h3>
            <p class="highlight">${order.customer_name || '-'}</p>
            ${order.customer_reference ? `<p>Ref: ${order.customer_reference}</p>` : ''}
            ${order.sage_customer_code ? `<p>Sage Code: ${order.sage_customer_code}</p>` : ''}
            ${order.booking_contacts && order.booking_contacts.length > 0 ? `<p>Contact: ${order.booking_contacts.join(', ')}</p>` : ''}
          </div>
        </div>
        
        <div class="dates-grid">
          <div class="date-box">
            <label>Order Date</label>
            <div class="value">${formatDate(order.order_date)}</div>
          </div>
          <div class="date-box">
            <label>Dispatch Date</label>
            <div class="value">${today}</div>
          </div>
          <div class="date-box">
            <label>Due Date</label>
            <div class="value">${formatDate(order.due_date)}</div>
          </div>
        </div>
        
        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 5%">Line</th>
              <th style="width: 20%">Product Code</th>
              <th style="width: 35%">Description</th>
              <th class="center" style="width: 10%">Qty</th>
              <th class="right" style="width: 15%">Unit Weight</th>
              <th class="right" style="width: 15%">Total Weight</th>
            </tr>
          </thead>
          <tbody>
            ${shippableLines.map((line, index) => `
              <tr>
                <td>${index + 1}</td>
                <td class="product-code">${line.product_display || '-'}</td>
                <td>
                  <div>${line.product_name || '-'}</div>
                </td>
                <td class="center"><strong>${line.toShip}</strong></td>
                <td class="right">${line.productWeight > 0 ? line.productWeight.toFixed(2) + ' kg' : '-'}</td>
                <td class="right"><strong>${line.lineWeight > 0 ? line.lineWeight.toFixed(2) + ' kg' : '-'}</strong></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals-section">
          <div class="totals-box">
            <div class="totals-row">
              <span>Total Lines:</span>
              <span>${shippableLines.length}</span>
            </div>
            <div class="totals-row">
              <span>Total Items:</span>
              <span>${totalItems}</span>
            </div>
            <div class="totals-row total">
              <span>Total Weight:</span>
              <span>${totalWeight.toFixed(2)} kg</span>
            </div>
          </div>
        </div>
        
        ${order.notes ? `
          <div class="notes-section">
            <h3>Order Notes</h3>
            <p>${order.notes}</p>
          </div>
        ` : ''}
        
        <div class="delivery-details">
          <h3>Important Delivery Details</h3>
          <p>Please check goods on arrival and if packing has obvious damage, please inspect the contents before signing for the goods.</p>
          <p class="warning">Any damage found or wrong goods ordered, must be reported within 48 hours of delivery.</p>
        </div>

        <div class="footer">
          <p>Generated on ${formatDateTime(new Date().toISOString())} | Order: ${order.order_number}</p>
          <p>Gopak Limited</p>
        </div>
        
        <script>
          // Only auto-print if opened directly in browser (fallback mode)
          // Don't print if inside an iframe (QZ Tray mode)
          if (window.opener === null && window.self === window.top) {
            window.onload = function() { window.print(); }
          }
        </script>
      </body>
      </html>
    `;
  };

  const generatePackingSlip = async () => {
    console.log('Order:', order);
    console.log('Order lines:', order?.lines);
    console.log('Shippable lines:', getShippableLines());
    setGenerating(true);
    
    try {
      const htmlContent = buildHtmlContent();
      const result = await printHtml(htmlContent, 'packingSlip');
      
      if (result.method === 'qz') {
        toast.success(`Packing slip sent to ${result.printer}`);
      }
      // Browser print doesn't need a toast - user sees the dialog
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to print packing slip');
    } finally {
      setGenerating(false);
    }
  };

  if (!order) return null;

  return (
    <Button
      variant={variant}
      color={color}
      size={size}
      startIcon={showIcon ? (generating ? <CircularProgress size={20} /> : <Print />) : null}
      onClick={generatePackingSlip}
      disabled={generating}
    >
      {generating ? 'Printing...' : label}
    </Button>
  );
}